import { TaskItem } from '@/components/tasks/task-line';
import { TaskItemV2 } from '@/components/tasks/task-line-v2.tsx';
import {
  ALL_FILTER,
  TasksFilter,
  type TasksPageFilters,
} from '@/components/tasks/tasks-filter.tsx';
import { TasksListV2 } from '@/components/tasks/tasks-list-v2.tsx';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import { getEntityTypeLabel, pluralize } from '@/lib/utils';
import { AssignedTask, TaskV2 } from '@/models/registry';
import { assignedTaskStore, dealStore } from '@/models/stores';
import { taskStoreV2 } from '@/models/stores/tasks-v2.ts';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { compact, isEmpty } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';

export const Route = createFileRoute('/(authenticated)/tasks/')({
  component: TasksPageWrapper,
  staticData: {
    keepAlive: true,
  },
});

type TasksListProps = {
  tasks: AssignedTask[] | TaskV2[];
};

const TasksList = observer(({ tasks }: TasksListProps) => {
  if (tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <p className="text-lg">No tasks found</p>
          <p className="text-sm mt-1">All tasks are complete!</p>
        </div>
      </div>
    );
  }

  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const task = tasks[index];
    const deal =
      task instanceof TaskV2
        ? dealStore.getById(task.taskable_id)
        : dealStore.getById(task.deal_id);

    return (
      <div style={style}>
        {task instanceof TaskV2 ? (
          <TaskItemV2 task={task} />
        ) : (
          <TaskItem task={task} deal={deal} />
        )}
      </div>
    );
  };

  return (
    <div className="h-full">
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            width={width}
            itemCount={tasks.length}
            itemSize={73}
          >
            {Row}
          </List>
        )}
      </AutoSizer>
    </div>
  );
});

const TasksPage = observer(() => {
  const { isFeatureEnabled: hasAllTasksAccess } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.ALL_TASKS,
  });
  const { isFeatureEnabled: isGeneralTasksEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.GENERALIZED_TASKS,
  });

  const allTasks = isGeneralTasksEnabled
    ? taskStoreV2.allForActiveDeals
    : assignedTaskStore.allForActiveDeals;

  const uniqueDealCount = useMemo(() => {
    const uniqueDealIds = new Set(
      allTasks.map((task) =>
        task instanceof AssignedTask ? task.deal_id : task.taskable_id,
      ),
    );
    return uniqueDealIds.size;
  }, [allTasks]);

  const uniqueAssigneeCount = useMemo(() => {
    const uniqueAssigneeIds = new Set(allTasks.map((task) => task.assignee_id));
    return uniqueAssigneeIds.size;
  }, [allTasks]);

  if (!hasAllTasksAccess) {
    return <Navigate to="/" />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none p-6 border-b border-gray-200 dark:border-gray-800">
        <h1 className="text-2xl  text-gray-900 dark:text-white">All Tasks</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {allTasks.length} {pluralize(allTasks.length, 'task', 'tasks')} across{' '}
          {uniqueDealCount} {pluralize(uniqueDealCount, 'deal', 'deals')}{' '}
          assigned to {uniqueAssigneeCount}{' '}
          {pluralize(uniqueAssigneeCount, 'person', 'people')}
        </p>
      </div>

      <div className="flex-1 min-h-0">
        <TasksList tasks={allTasks} />
      </div>
    </div>
  );
});

function TasksPageWrapper() {
  const { isFeatureEnabled: isGeneralTasksEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.GENERALIZED_TASKS,
  });

  return isGeneralTasksEnabled ? <TasksPageV2 /> : <TasksPage />;
}

const TasksPageV2 = observer(() => {
  const { isFeatureEnabled: hasAllTasksAccess } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.ALL_TASKS,
  });
  const [filters, setFilters] = useState<TasksPageFilters>({
    entityTypes: [ALL_FILTER],
    statuses: [ALL_FILTER],
  });

  const filteredTasks = useMemo(() => {
    let tasks = taskStoreV2.allTasksForActiveEntities;

    if (filters.assigneeIds?.length) {
      tasks = tasks.filter((task) => {
        const taskAssignee = task.assignee_id || 'unassigned';
        return filters.assigneeIds?.includes(taskAssignee);
      });
    }

    if (
      !isEmpty(filters.entityTypes) &&
      !filters.entityTypes?.includes(ALL_FILTER)
    ) {
      tasks = tasks.filter((task) =>
        filters.entityTypes!.includes(task.taskable_type),
      );
    }

    if (!isEmpty(filters.statuses) && !filters.statuses?.includes(ALL_FILTER)) {
      tasks = tasks.filter((task) => filters.statuses!.includes(task.status));
    }

    const query = filters.searchQuery;
    if (query) {
      tasks = tasks.filter((t) =>
        t.title.toLowerCase().includes(query.toLowerCase()),
      );
    }

    return tasks;
  }, [filters, taskStoreV2.allTasksForActiveEntities]);

  const taskCounts = useMemo(() => {
    const uniqueAssigneeIds = new Set(
      compact(filteredTasks.map((task) => task.assignee_id)),
    );

    const entityCounts: Record<string, Set<string>> = {};
    filteredTasks.forEach((task) => {
      if (!entityCounts[task.taskable_type]) {
        entityCounts[task.taskable_type] = new Set();
      }
      entityCounts[task.taskable_type].add(task.taskable_id);
    });

    const totalEntities = Object.values(entityCounts).reduce(
      (sum, set) => sum + set.size,
      0,
    );

    return {
      total: filteredTasks.length,
      uniqueAssignees: uniqueAssigneeIds.size,
      uniqueEntities: totalEntities,
      byType: entityCounts,
    };
  }, [filteredTasks]);

  if (!hasAllTasksAccess) {
    return <Navigate to="/" />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl">All Tasks</h1>
        </div>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {taskCounts.total} {pluralize(taskCounts.total, 'task', 'tasks')}{' '}
          across{' '}
          {Object.keys(taskCounts.byType).length === 1 ? (
            <>
              {taskCounts.uniqueEntities}{' '}
              {getEntityTypeLabel(
                Object.keys(taskCounts.byType)[0],
                taskCounts.uniqueEntities,
              )}
            </>
          ) : taskCounts.uniqueEntities > 0 ? (
            <>
              {Object.entries(taskCounts.byType).map(([type, ids], index) => {
                const count = ids.size;
                const isLast =
                  index === Object.entries(taskCounts.byType).length - 1;
                return (
                  <span key={type}>
                    {count} {getEntityTypeLabel(type, count)}
                    {!isLast && ', '}
                  </span>
                );
              })}
            </>
          ) : (
            '0 entities'
          )}{' '}
          assigned to {taskCounts.uniqueAssignees}{' '}
          {pluralize(taskCounts.uniqueAssignees, 'person', 'people')}
        </p>
      </div>

      <div className="flex-none px-6 py-3 border-b">
        <TasksFilter filters={filters} onChange={setFilters} />
      </div>

      <div className="flex-1 min-h-0">
        <TasksListV2 tasks={filteredTasks} />
      </div>
    </div>
  );
});
